import { useEffect, useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/utils/format';

interface PaymentOtpModalProps {
  open: boolean;
  payeeName: string;
  amount: number;
  currency: string;
  busy: boolean;
  error: string;
  onVerify: (otp: string) => void;
  onCancel: () => void;
}

/** The 6-digit code production texts before a payment leaves the wallet. */
export default function PaymentOtpModal({
  open,
  payeeName,
  amount,
  currency,
  busy,
  error,
  onVerify,
  onCancel,
}: PaymentOtpModalProps) {
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (open) setOtp('');
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      icon="ri-shield-keyhole-line"
      title="OTP Verification Required"
      subtitle={`Enter the 6-digit code sent to your phone to send ${formatMoney(amount, currency)} to ${payeeName}.`}
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button icon="ri-checkbox-circle-line" onClick={() => onVerify(otp)} disabled={otp.length !== 6 || busy}>
            Verify and Send
          </Button>
        </>
      }
    >
      <label htmlFor="single-otp" className="text-xs font-label font-medium text-foreground-600">
        Enter OTP Code
      </label>
      <input
        id="single-otp"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={otp}
        onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        className="mt-1.5 h-14 w-full rounded-md border border-background-300 bg-background-50 text-center font-heading text-2xl tracking-[0.6em] text-foreground-900 placeholder:text-foreground-300 focus:border-primary-400 focus:outline-none"
      />
      {error ? (
        <p role="alert" className="mt-2 flex items-center gap-1.5 text-sm text-primary-800">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
